// Apps Script webhook sync — pulls remote stats and pushes local dirty
// keys. The remote authority is a Google Sheet managed by a user-
// deployable Apps Script. Because the webhook is cross-origin from the
// app's host, every request uses `Content-Type: text/plain` to skip
// the CORS preflight (Apps Script does NOT respond to OPTIONS).
//
// The controller owns no state of its own; its dependencies are passed
// in via `createSyncController`. This keeps the module trivially
// testable and lets the caller swap fetch/beacon/storage for tests.

import type { CardStat, StatsMap, SyncStatus } from "../types";

/** Runtime dependencies — everything the controller needs from the
 *  host app. Mirrors the legacy god-object fields it used to touch. */
export type SyncDeps = {
  /** Webhook URL — Apps Script /exec. */
  getWebhookUrl: () => string;
  /** Shared secret — passed in every request body. */
  getWebhookSecret: () => string;
  /** Identifies the user. Apps Script scopes stats per user_id. */
  getUserName: () => string;
  /** Active deck id. Included in every sync payload so the Apps Script
   *  can route reads/writes to the correct stats tab (one Apps Script
   *  per org, multiple decks per Apps Script). Future Supabase port
   *  keeps the same shape. */
  getListId: () => string;
  /** Current stats map — mutated in-place by merges. */
  getStats: () => StatsMap;
  /** Keys dirty since last successful push. */
  getDirty: () => Set<string>;
  /** Persists stats to localStorage after a merge. */
  saveStats: (s: StatsMap) => void;
  /** Optional: cardIds present in the currently-loaded deck. When set,
   *  `pull()` drops remote entries outside this set so stats belonging
   *  to other decks never contaminate the active slice. */
  getKnownKeys?: () => ReadonlySet<string>;
  /** Surfaces status transitions (drives the footer dot). */
  setStatus: (s: SyncStatus) => void;
  /** Read-only — so status transitions know if still dirty. */
  getStatus: () => SyncStatus;
};

/** Remote rows from Apps Script: snake_case headers, ISO date strings. */
type RemoteRow = Partial<{
  attempts: number | string;
  correct: number | string;
  box: number | string;
  dueAt: number | string;
  due_at: number | string;
  lastSeenAt: number | string;
  last_seen_at: number | string;
  lastFailAt: number | string;
  last_fail_at: number | string;
}>;

/** Coerce Apps Script date-ish values to Unix millis. */
function toMillis(v: unknown): number {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  const s = String(v);
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : 0;
}

/** Normalize a remote row to the camelCase + millis shape we use locally. */
export function normalizeRemoteStat(r: RemoteRow): CardStat {
  return {
    attempts: Number(r.attempts) || 0,
    correct: Number(r.correct) || 0,
    box: Number(r.box) || 0,
    dueAt: toMillis(r.dueAt ?? r.due_at),
    lastSeenAt: toMillis(r.lastSeenAt ?? r.last_seen_at),
    lastFailAt: toMillis(r.lastFailAt ?? r.last_fail_at),
  };
}

/** Merge remote stats into `local` in-place. Returns number of rows
 *  that changed so the caller can decide whether to persist. "Newer
 *  lastSeenAt wins" — if unknown locally, remote takes the slot.
 *
 *  `knownKeys`, when provided, restricts the merge to cardIds known in
 *  the current deck. This prevents cross-deck pollution: the Apps Script
 *  webhook stores stats per user_id (not per deck), so a pull returns
 *  every cardId the user has ever touched, including cards from other
 *  decks. Passing the current deck's cardId set keeps the active slice
 *  clean. `undefined` keeps the v1 "merge everything" behaviour for
 *  callers that don't care (mainly older tests). */
export function mergeRemoteStats(
  local: StatsMap,
  remote: Record<string, RemoteRow>,
  knownKeys?: ReadonlySet<string>,
): number {
  let changed = 0;
  for (const cardKey in remote) {
    if (knownKeys && !knownKeys.has(cardKey)) continue;
    const r = normalizeRemoteStat(remote[cardKey]!);
    const l = local[cardKey];
    if (!l || (r.lastSeenAt || 0) > (l.lastSeenAt || 0)) {
      local[cardKey] = r;
      changed++;
    }
  }
  return changed;
}

export type SyncController = {
  /** True when the user/webhook/secret trio is fully configured. */
  enabled: () => boolean;
  /** Fetch remote stats and merge into local. */
  pull: () => Promise<void>;
  /** Push all dirty keys. Optimistically clears dirty; re-adds on failure. */
  push: () => Promise<void>;
  /** Debounce: coalesce many recordResult() calls into one push. */
  schedule: (delayMs?: number) => void;
  /** Best-effort sync on page hide/unload using sendBeacon. */
  flushBeacon: () => void;
};

export function createSyncController(deps: SyncDeps): SyncController {
  // Debounce timer owned by the controller so each instance is
  // independently schedulable (handy for tests).
  let timer: ReturnType<typeof setTimeout> | null = null;

  function enabled(): boolean {
    return Boolean(deps.getUserName() && deps.getWebhookUrl() && deps.getWebhookSecret());
  }

  async function pull(): Promise<void> {
    if (!enabled()) return;
    deps.setStatus("syncing");
    try {
      const res = await fetch(deps.getWebhookUrl(), {
        method: "POST",
        headers: { "Content-Type": "text/plain" }, // avoids CORS preflight
        body: JSON.stringify({
          action: "getStats",
          secret: deps.getWebhookSecret(),
          user_id: deps.getUserName(),
          list_id: deps.getListId(),
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "pull failed");
      const changed = mergeRemoteStats(
        deps.getStats(),
        data.stats || {},
        deps.getKnownKeys?.(),
      );
      if (changed) deps.saveStats(deps.getStats());
      deps.setStatus(deps.getDirty().size ? "pending" : "synced");
    } catch (err) {
      console.warn("[sync] pull failed:", err);
      deps.setStatus("error");
    }
  }

  async function push(): Promise<void> {
    const dirty = deps.getDirty();
    if (!enabled() || !dirty.size) return;
    const dirtyKeys = Array.from(dirty);
    const payload: StatsMap = {};
    const stats = deps.getStats();
    for (const k of dirtyKeys) {
      const v = stats[k];
      if (v) payload[k] = v;
    }
    // Optimistically clear (we re-add on failure).
    dirty.clear();
    deps.setStatus("syncing");
    try {
      const res = await fetch(deps.getWebhookUrl(), {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({
          action: "putStats",
          secret: deps.getWebhookSecret(),
          user_id: deps.getUserName(),
          list_id: deps.getListId(),
          stats: payload,
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "push failed");
      deps.setStatus(deps.getDirty().size ? "pending" : "synced");
    } catch (err) {
      console.warn("[sync] push failed:", err);
      // Re-add keys so they get retried.
      for (const k of dirtyKeys) dirty.add(k);
      deps.setStatus("error");
    }
  }

  function schedule(delayMs = 5000): void {
    if (!enabled()) return;
    deps.setStatus("pending");
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      push();
    }, delayMs);
  }

  function flushBeacon(): void {
    const dirty = deps.getDirty();
    if (!enabled() || !dirty.size) return;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    const payload: StatsMap = {};
    const stats = deps.getStats();
    for (const k of dirty) {
      const v = stats[k];
      if (v) payload[k] = v;
    }
    try {
      const blob = new Blob(
        [
          JSON.stringify({
            action: "putStats",
            secret: deps.getWebhookSecret(),
            user_id: deps.getUserName(),
            list_id: deps.getListId(),
            stats: payload,
          }),
        ],
        { type: "text/plain" },
      );
      if (navigator.sendBeacon(deps.getWebhookUrl(), blob)) {
        dirty.clear();
      }
    } catch (err) {
      console.warn("[sync] beacon failed:", err);
    }
  }

  return { enabled, pull, push, schedule, flushBeacon };
}
